import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    type Ref,
} from "react";
import { createEditor, Editor, Range, Transforms } from "slate";
import {
    Slate,
    Editable,
    withReact,
    ReactEditor,
    RenderLeafProps,
    RenderElementProps,
    RenderPlaceholderProps,
} from "slate-react";
import classNames from "classnames";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { Popover, PopoverContent } from "@design-system/Popover/Popover";
import { WorkspaceMember } from "@common/types/workspace";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { Button } from "@design-system/Button/Button";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { ArrowUplongIcon, DocumentIcon, MentionIcon, SlashIcon } from "@icons";
import { FINANCIAL_DOCUMENT_TYPES } from "@common/constants/financial-documents";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { SpreadsheetReference } from "@common/schemas/spreadsheetReference.schema";
import { TransactionReference } from "@common/schemas/transactionReference.schema";
import { CounterpartyReference } from "@common/schemas/counterpartyReference.schema";
import { InvoiceReference } from "@common/schemas/invoiceReference.schema";
import { DocumentReference } from "@common/schemas/documentReference.schema";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import {
    DraftCounterpartyRefTag,
    DraftDocumentRefTag,
    DraftInvoiceRefTag,
    DraftSpreadsheetRefTag,
    DraftTransactionRefTag,
} from "../DraftRefTags";
import { buildRefKey } from "../spreadsheetRefUtils";
import { fileKey } from "../fileAttachmentUtils";
import { type SlateValue, createEmptySlateValue } from "./slateTypes";
import { wrapLink } from "./SlateEditor.lib";
import {
    withInlines,
    insertMention,
    insertEntityRef,
} from "./SlateEditor.inlines";
import {
    handleListKeyDown,
    toggleChecklist,
    toggleList,
    withLists,
} from "./SlateEditor.lists";
import { Element, Leaf } from "./SlateEditor.elements";
import { ComposerInsertMenu } from "./ComposerInsertMenu";
import {
    OrganizationScopeLabel,
    OrganizationScopePicker,
} from "./OrganizationScopeStrip";
import { HoveringToolbar } from "./HoveringToolbar";
import styles from "./SlateEditor.module.scss";
import { LinkInputModal } from "./LinkInputModal";
import { toggleMark } from "./SlateEditor.marks";
import { positionPopoverAtCursor } from "./SlateEditor.popover";
import { useSlashCommands } from "./useSlashCommands";
import { useNextFrame } from "./useNextFrame";
import { SlashCommandPopover } from "./SlashCommandPopover";
import { getSlashTrigger, insertSlashTrigger } from "./slashTrigger";
import { handleListNavigation } from "./listNavigation";
import { EntityRefPopover } from "./entityRef/EntityRefPopover";
import { useTaskLinkPasteResolver } from "./entityRef/useTaskLinkPasteResolver";
import type { EntityRefOption } from "./entityRef/entityRefOptions.types";
import {
    ENTITY_REF_EMPTY_COPY,
    useEntityRefOptions,
} from "./entityRef/useEntityRefOptions";
import { hasSlateContent } from "./slateHelpers";

interface SlateEditorProps {
    value?: SlateValue;
    onChange?: (value: SlateValue) => void;
    /**
     * Called on send. May be async (e.g. uploading attachments first); return
     * `false` to signal the send failed so the editor keeps the typed content
     * for retry. A `void`/`true` result clears the editor.
     */
    onSubmit?: (
        value: SlateValue,
        organizationId?: string | null,
    ) => void | Promise<boolean>;
    placeholder?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    className?: string;
    hideKeyboardShortcut?: boolean;
    placeholderClassName?: string;
    initialOrganizationScopedMessage?: boolean;
    hideOrganizationScopePicker?: boolean;
    isEditing?: boolean;
    /** Escape leaves the editor (keeping the draft) once no mention or slash menu is open */
    blurOnEscape?: boolean;
    /** Draft spreadsheet references shown above the editor when composing a new message */
    draftRefs?: SpreadsheetReference[];
    /** Called when the user removes a draft ref chip */
    onRemoveDraftRef?: (index: number) => void;
    draftTransactionRefs?: TransactionReference[];
    onRemoveDraftTransactionRef?: (index: number) => void;
    draftCounterpartyRefs?: CounterpartyReference[];
    onRemoveDraftCounterpartyRef?: (index: number) => void;
    draftInvoiceRefs?: InvoiceReference[];
    onRemoveDraftInvoiceRef?: (index: number) => void;
    draftDocumentRefs?: DocumentReference[];
    onRemoveDraftDocumentRef?: (index: number) => void;
    /** Called when the user selects the /task slash command */
    onCreateTask?: () => void;
    displayActions?: boolean;
    /** Files staged to be uploaded and attached when the message is sent */
    pendingAttachments?: File[];
    /** Called when the user picks or drops files onto the editor */
    onAddAttachments?: (files: File[]) => void;
    /** Called when the user removes a pending attachment chip */
    onRemoveAttachment?: (index: number) => void;
    /** True while staged attachments are being uploaded on send */
    isUploadingAttachments?: boolean;
    /** Shows the drop highlight while dragged rows are over this composer */
    isDropTarget?: boolean;
    /** Lets the parent move focus into the editor (e.g. after a drop) */
    focusRef?: Ref<SlateEditorHandle>;
}

/** Imperative handle a parent gets through `focusRef`. */
export interface SlateEditorHandle {
    /** Focuses the editor, with the caret at the end when it has no selection. */
    focus: () => void;
}

/** Stable remove callback for a pending attachment tag. */
const PendingAttachmentTag: React.FC<{
    file: File;
    index: number;
    onRemove?: (index: number) => void;
}> = React.memo(function PendingAttachmentTag({ file, index, onRemove }) {
    const handleRemove = useCallback(
        () => onRemove?.(index),
        [onRemove, index],
    );
    return (
        <Tag
            size="md"
            color="primary"
            outline
            leftIcon={<DocumentIcon />}
            onRemove={onRemove ? handleRemove : undefined}
            removeLabel={`Remove attachment ${file.name}`}
        >
            {file.name}
        </Tag>
    );
});

/** Stable remove-callback wrapper so the memoized `Component` skips re-renders. */
function DraftRefTag<TRef>({
    reference,
    index,
    onRemove,
    Component,
}: {
    reference: TRef;
    index: number;
    onRemove: (index: number) => void;
    Component: React.ComponentType<{ reference: TRef; onRemove?: () => void }>;
}) {
    const handleRemove = useCallback(() => onRemove(index), [onRemove, index]);
    return <Component reference={reference} onRemove={handleRemove} />;
}

/** Focuses the editor, puts the caret at the end when there is none, then inserts. */
function focusAndInsert(editor: ReactEditor, insert: (target: Editor) => void) {
    ReactEditor.focus(editor);
    if (!editor.selection) {
        Transforms.select(editor, Editor.end(editor, []));
    }
    insert(editor);
}

type TriggerChar = "@" | "/";

/** Range of the trigger character right before a collapsed caret, if it is `trigger`. */
function getTriggerCharRange(
    editor: Editor,
    trigger: TriggerChar,
): Range | null {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
        return null;
    }
    const start = Range.start(selection);
    const charBefore = Editor.before(editor, start, { unit: "character" });
    if (!charBefore) {
        return null;
    }
    const range = Editor.range(editor, charBefore, start);
    return Editor.string(editor, range) === trigger ? range : null;
}

export const SlateEditor: React.FC<SlateEditorProps> = ({
    value,
    onChange,
    onSubmit,
    placeholder = "Leave a comment...",
    disabled,
    autoFocus,
    className,
    hideKeyboardShortcut,
    placeholderClassName,
    initialOrganizationScopedMessage = false,
    hideOrganizationScopePicker = false,
    isEditing = false,
    blurOnEscape = false,
    draftRefs,
    onRemoveDraftRef,
    draftTransactionRefs,
    onRemoveDraftTransactionRef,
    draftCounterpartyRefs,
    onRemoveDraftCounterpartyRef,
    draftInvoiceRefs,
    onRemoveDraftInvoiceRef,
    draftDocumentRefs,
    onRemoveDraftDocumentRef,
    onCreateTask,
    displayActions = true,
    pendingAttachments,
    onAddAttachments,
    onRemoveAttachment,
    isUploadingAttachments = false,
    isDropTarget = false,
    focusRef,
}) => {
    const editorContainerRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isDraggingFilesOver, setIsDraggingFilesOver] = useState(false);
    const popoverTargetRef = useRef<HTMLSpanElement | null>(null);
    const slashPopoverTargetRef = useRef<HTMLSpanElement | null>(null);
    const organization = useActiveOrganization();
    const [isOrganizationScopedMessage, setIsOrganizationScopedMessage] =
        useState(initialOrganizationScopedMessage);
    const resolveTaskLinkPaste = useTaskLinkPasteResolver();
    const resolveTaskLinkPasteRef = useLatestRef(resolveTaskLinkPaste);
    const editor = useMemo(
        () =>
            withLists(
                withInlines(withReact(createEditor()), {
                    resolvePastedEntityRef: (text) =>
                        resolveTaskLinkPasteRef.current(text),
                }),
            ),
        [resolveTaskLinkPasteRef],
    );
    // Use a ref for initial value to avoid Slate re-initialization issues
    const initialValueRef = useRef<SlateValue>(
        value ? structuredClone(value) : createEmptySlateValue(),
    );
    const [hasEditorContent, setHasEditorContent] = useState(() =>
        hasSlateContent(initialValueRef.current),
    );

    // Reset org-scoped state when organization context is lost
    useEffect(() => {
        if (!organization) {
            setIsOrganizationScopedMessage(false);
        }
    }, [organization]);

    // Mention state
    const [mentionTarget, setMentionTarget] = useState<Range | null>(null);
    const [mentionSearch, setMentionSearch] = useState("");
    const [mentionIndex, setMentionIndex] = useState(0);
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    // Slash commands
    const {
        slashTarget,
        setSlashTarget,
        slashSearch,
        setSlashSearch,
        slashQuery,
        setSlashQuery,
        slashIndex,
        setSlashIndex,
        slashCommands,
        filteredCommands,
        activeEntityKind,
        selectSlashCommand,
        resetSlashState,
    } = useSlashCommands({
        editor,
        isEditing,
        hasOrganization: !!organization,
        canChangeOrganizationScope: !hideOrganizationScopePicker,
        setIsOrganizationScopedMessage,
        onCreateTask,
    });

    const isEntityStage = !!slashTarget && activeEntityKind !== null;
    const { options: entityOptions, isLoading: isEntityLoading } =
        useEntityRefOptions({
            kind: activeEntityKind,
            query: slashQuery ?? "",
        });

    // Link modal state
    const [showLinkModal, setShowLinkModal] = useState(false);

    const { data: workspaceMembers } = useCurrentWorkspaceMembers();

    // Filter workspace members based on search
    const filteredMembers = useMemo(() => {
        if (!workspaceMembers) {
            return [];
        }
        return workspaceMembers
            .filter(
                (member) =>
                    member.user.firstName
                        .toLowerCase()
                        .includes(mentionSearch.toLowerCase()) ||
                    member.user.name
                        .toLowerCase()
                        .includes(mentionSearch.toLowerCase()),
            )
            .slice(0, 10);
    }, [workspaceMembers, mentionSearch]);

    // Position the mention popover target at the cursor
    useEffect(() => {
        if (mentionTarget && filteredMembers.length > 0) {
            positionPopoverAtCursor({
                editor,
                target: mentionTarget,
                anchorRef: popoverTargetRef,
                containerRef: editorContainerRef,
            });
        }
    }, [editor, mentionTarget, filteredMembers.length, mentionSearch]);

    // Position the slash command popover target at the cursor
    useEffect(() => {
        if (slashTarget && (filteredCommands.length > 0 || isEntityStage)) {
            positionPopoverAtCursor({
                editor,
                target: slashTarget,
                anchorRef: slashPopoverTargetRef,
                containerRef: editorContainerRef,
                edge: "top",
            });
        }
    }, [
        editor,
        slashTarget,
        filteredCommands.length,
        slashSearch,
        slashQuery,
        isEntityStage,
    ]);

    const focusEditor = useCallback(() => {
        ReactEditor.focus(editor);
        if (!editor.selection) {
            Transforms.select(editor, Editor.end(editor, []));
        }
    }, [editor]);

    useImperativeHandle(focusRef, () => ({ focus: focusEditor }), [
        focusEditor,
    ]);

    // The strip's picker unmounts when the message turns external, so focus
    // moves back to the text a frame later instead of falling to the body.
    const focusEditorNextFrame = useNextFrame(focusEditor);
    const handleIsInternalChange = useCallback(
        (isInternal: boolean) => {
            setIsOrganizationScopedMessage(isInternal);
            focusEditorNextFrame();
        },
        [focusEditorNextFrame],
    );

    useEffect(() => {
        if (!autoFocus || disabled) {
            return;
        }

        // Slate's autoFocus can be unreliable depending on mount timing,
        // so we explicitly focus on the next frame.
        const frameId = requestAnimationFrame(focusEditor);

        return () => cancelAnimationFrame(frameId);
    }, [autoFocus, disabled, focusEditor]);

    const renderLeaf = useCallback(
        (props: RenderLeafProps) => <Leaf {...props} />,
        [],
    );
    const renderElement = useCallback(
        (props: RenderElementProps) => (
            <Element {...props} workspaceMembers={workspaceMembers} />
        ),
        [workspaceMembers],
    );
    const renderPlaceholder = useCallback(
        (props: RenderPlaceholderProps) => (
            <span
                {...props.attributes}
                className={classNames(
                    styles.slateEditorPlaceholder,
                    placeholderClassName,
                )}
            >
                {placeholder}
            </span>
        ),
        [placeholder, placeholderClassName],
    );

    const openMention = useCallback(
        ({ target, search }: { target: Range; search: string }) => {
            setMentionTarget(target);
            setMentionSearch(search);
            setMentionIndex(0);
            setSlashTarget(null);
        },
        [setSlashTarget],
    );

    const openSlashCommands = useCallback(
        (slashTrigger: NonNullable<ReturnType<typeof getSlashTrigger>>) => {
            setSlashTarget(slashTrigger.range);
            setSlashSearch(slashTrigger.command);
            setSlashQuery(slashTrigger.query);
            setSlashIndex(0);
            setMentionTarget(null);
        },
        [setSlashTarget, setSlashSearch, setSlashQuery, setSlashIndex],
    );

    const handleChange = useCallback(
        (newValue: SlateValue) => {
            onChange?.(newValue);
            setHasEditorContent(hasSlateContent(newValue));

            // Check for @ mention and / command triggers
            const { selection } = editor;
            if (selection && Range.isCollapsed(selection)) {
                const [start] = Range.edges(selection);
                const mentionTriggerRange = getTriggerCharRange(editor, "@");
                if (mentionTriggerRange) {
                    openMention({ target: mentionTriggerRange, search: "" });
                    return;
                }

                const wordBefore = Editor.before(editor, start, {
                    unit: "word",
                });
                const before = wordBefore && Editor.before(editor, wordBefore);
                const beforeRange =
                    before && Editor.range(editor, before, start);
                const beforeText =
                    beforeRange && Editor.string(editor, beforeRange);
                const beforeMatch = beforeText && /^@(\w*)$/.exec(beforeText);

                // Check what comes after the cursor (handle end of document)
                const after = Editor.after(editor, start);
                let afterMatch = true; // Default to true if at end of document
                if (after) {
                    const afterRange = Editor.range(editor, start, after);
                    const afterText = Editor.string(editor, afterRange);
                    afterMatch = /^(\s|$)/.test(afterText);
                }

                if (beforeRange && beforeMatch && afterMatch) {
                    openMention({
                        target: beforeRange,
                        search: beforeMatch[1],
                    });
                    return;
                }

                const slashTrigger =
                    slashCommands.length > 0 ? getSlashTrigger(editor) : null;
                const isSlashTriggerActive =
                    slashTrigger !== null &&
                    (slashTrigger.query === null ||
                        slashCommands.some(
                            (command) =>
                                command.id === slashTrigger.command &&
                                command.entityKind !== undefined,
                        ));
                if (slashTrigger && isSlashTriggerActive) {
                    openSlashCommands(slashTrigger);
                    return;
                }
            }
            setMentionTarget(null);
            setSlashTarget(null);
            setSlashQuery(null);
        },
        [
            onChange,
            editor,
            slashCommands,
            openMention,
            openSlashCommands,
            setSlashTarget,
            setSlashQuery,
        ],
    );

    const selectEntityRef = useCallback(
        (option: EntityRefOption) => {
            if (!slashTarget) {
                return;
            }
            Transforms.select(editor, slashTarget);
            insertEntityRef(editor, option.entityRef);
            resetSlashState();
        },
        [editor, slashTarget, resetSlashState],
    );

    const selectMention = useCallback(
        (member: WorkspaceMember) => {
            if (mentionTarget) {
                Transforms.select(editor, mentionTarget);
                insertMention(editor, member);
                setMentionTarget(null);
            }
        },
        [editor, mentionTarget],
    );

    const hasPendingAttachments = Boolean(pendingAttachments?.length);

    const submitMessage = useCallback(async () => {
        if (
            disabled ||
            !onSubmit ||
            (!hasEditorContent && !hasPendingAttachments) ||
            isUploadingAttachments
        ) {
            return;
        }

        const organizationId = isOrganizationScopedMessage
            ? organization?.id
            : null;

        const submitResult = onSubmit(
            structuredClone(editor.children),
            organizationId,
        );
        // Async callers (attachment uploads) resolve `false` on failure so we
        // preserve the typed content for retry instead of wiping it.
        const didSubmit =
            submitResult instanceof Promise ? await submitResult : true;
        if (!didSubmit) {
            return;
        }
        // Reset editor properly using Transforms
        Transforms.delete(editor, {
            at: {
                anchor: Editor.start(editor, []),
                focus: Editor.end(editor, []),
            },
        });
        // Ensure we have at least one empty paragraph
        if (editor.children.length === 0) {
            Transforms.insertNodes(editor, {
                type: "paragraph",
                children: [{ text: "" }],
            });
        }
        // Move cursor to start
        Transforms.select(editor, Editor.start(editor, []));
        setHasEditorContent(false);
    }, [
        disabled,
        onSubmit,
        hasEditorContent,
        hasPendingAttachments,
        isUploadingAttachments,
        editor,
        isOrganizationScopedMessage,
        organization?.id,
    ]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (isEntityStage) {
                const isHandled = handleListNavigation({
                    event,
                    count: entityOptions.length,
                    activeIndex: slashIndex,
                    setActiveIndex: setSlashIndex,
                    onSelect: (index) => selectEntityRef(entityOptions[index]),
                    onClose: resetSlashState,
                });
                if (isHandled) {
                    return;
                }
                if (event.key === "Enter" || event.key === "Tab") {
                    event.preventDefault();
                    return;
                }
            }

            if (
                !isEntityStage &&
                slashTarget &&
                filteredCommands.length > 0 &&
                handleListNavigation({
                    event,
                    count: filteredCommands.length,
                    activeIndex: slashIndex,
                    setActiveIndex: setSlashIndex,
                    onSelect: (index) =>
                        selectSlashCommand(filteredCommands[index].id),
                    onClose: resetSlashState,
                })
            ) {
                return;
            }

            if (
                mentionTarget &&
                filteredMembers.length > 0 &&
                handleListNavigation({
                    event,
                    count: filteredMembers.length,
                    activeIndex: mentionIndex,
                    setActiveIndex: setMentionIndex,
                    onSelect: (index) => selectMention(filteredMembers[index]),
                    onClose: () => setMentionTarget(null),
                })
            ) {
                return;
            }

            if (blurOnEscape && event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                ReactEditor.blur(editor);
                return;
            }

            const listResult = handleListKeyDown(editor, event);
            if (listResult === "handled") {
                event.preventDefault();
                return;
            }
            if (listResult === "allow-default") {
                return;
            }

            if (event.key === "Enter" && !event.shiftKey) {
                if (onSubmit) {
                    event.preventDefault();
                    submitMessage();
                }
                return;
            }

            // Keyboard shortcuts for formatting
            if (event.ctrlKey || event.metaKey) {
                switch (event.key) {
                    case "b":
                        event.preventDefault();
                        toggleMark(editor, "bold");
                        break;
                    case "i":
                        event.preventDefault();
                        toggleMark(editor, "italic");
                        break;
                    case "u":
                        event.preventDefault();
                        toggleMark(editor, "underline");
                        break;
                }
            }
        },
        [
            editor,
            mentionTarget,
            filteredMembers,
            mentionIndex,
            selectMention,
            slashTarget,
            filteredCommands,
            slashIndex,
            setSlashIndex,
            selectSlashCommand,
            resetSlashState,
            isEntityStage,
            entityOptions,
            selectEntityRef,
            blurOnEscape,
            submitMessage,
            onSubmit,
        ],
    );

    const isMentionPopoverOpen = !!mentionTarget && filteredMembers.length > 0;
    const isSlashPopoverOpen =
        !!slashTarget && !isEntityStage && filteredCommands.length > 0;

    // The popovers dismiss on an outside press, which can land before the
    // button's click. Snapshot whether one was open at press time so the click
    // can toggle instead of reopening. Keyboard clicks (`detail` 0) have no
    // press, so they read the live state.
    const triggerPressRef = useRef<{
        trigger: TriggerChar;
        wasOpen: boolean;
    } | null>(null);
    const consumeWasTriggerPopoverOpen = useCallback(
        ({
            trigger,
            event,
            isOpen,
        }: {
            trigger: TriggerChar;
            event: React.MouseEvent<HTMLButtonElement>;
            isOpen: boolean;
        }) => {
            const press = triggerPressRef.current;
            triggerPressRef.current = null;
            return event.detail !== 0 && press?.trigger === trigger
                ? press.wasOpen
                : isOpen;
        },
        [],
    );
    const handleMentionButtonPointerDown = useCallback(() => {
        triggerPressRef.current = {
            trigger: "@",
            wasOpen: isMentionPopoverOpen,
        };
    }, [isMentionPopoverOpen]);
    const handleSlashButtonPointerDown = useCallback(() => {
        triggerPressRef.current = { trigger: "/", wasOpen: isSlashPopoverOpen };
    }, [isSlashPopoverOpen]);

    const handleMentionButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            if (disabled) {
                return;
            }
            const wasOpen = consumeWasTriggerPopoverOpen({
                trigger: "@",
                event,
                isOpen: isMentionPopoverOpen,
            });
            if (wasOpen) {
                setMentionTarget(null);
                ReactEditor.focus(editor);
                return;
            }
            // The caret already follows a trigger: reopen instead of typing
            // another one.
            const triggerRange = getTriggerCharRange(editor, "@");
            if (triggerRange) {
                ReactEditor.focus(editor);
                openMention({ target: triggerRange, search: "" });
                return;
            }
            focusAndInsert(editor, (target) =>
                Transforms.insertText(target, "@"),
            );
        },
        [
            disabled,
            editor,
            isMentionPopoverOpen,
            consumeWasTriggerPopoverOpen,
            openMention,
        ],
    );

    const handleSlashButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            if (disabled) {
                return;
            }
            const wasOpen = consumeWasTriggerPopoverOpen({
                trigger: "/",
                event,
                isOpen: isSlashPopoverOpen,
            });
            if (wasOpen) {
                resetSlashState();
                ReactEditor.focus(editor);
                return;
            }
            const triggerRange = getTriggerCharRange(editor, "/");
            const slashTrigger = triggerRange ? getSlashTrigger(editor) : null;
            if (slashTrigger?.command === "" && slashTrigger.query === null) {
                ReactEditor.focus(editor);
                openSlashCommands(slashTrigger);
                return;
            }
            focusAndInsert(editor, insertSlashTrigger);
        },
        [
            disabled,
            editor,
            isSlashPopoverOpen,
            consumeWasTriggerPopoverOpen,
            resetSlashState,
            openSlashCommands,
        ],
    );

    const handleAttachFile = useCallback(() => {
        if (disabled) {
            return;
        }
        fileInputRef.current?.click();
    }, [disabled]);

    const applyEditorFormat = useCallback(
        (format: () => void) => {
            if (disabled) {
                return;
            }
            ReactEditor.focus(editor);
            if (!editor.selection) {
                Transforms.select(editor, Editor.end(editor, []));
            }
            format();
        },
        [disabled, editor],
    );

    const handleToggleChecklist = useCallback(() => {
        applyEditorFormat(() => toggleChecklist(editor));
    }, [applyEditorFormat, editor]);

    const handleToggleBulletList = useCallback(() => {
        applyEditorFormat(() => toggleList(editor, "bulleted-list"));
    }, [applyEditorFormat, editor]);

    const handleToggleNumberedList = useCallback(() => {
        applyEditorFormat(() => toggleList(editor, "numbered-list"));
    }, [applyEditorFormat, editor]);

    const handleFileInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(event.target.files ?? []);
            if (files.length > 0) {
                onAddAttachments?.(files);
            }
            // Allow re-selecting the same file
            event.target.value = "";
        },
        [onAddAttachments],
    );

    const handleDragOver = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            if (
                !onAddAttachments ||
                disabled ||
                isEditing ||
                !event.dataTransfer.types.includes("Files")
            ) {
                return;
            }
            event.preventDefault();
            setIsDraggingFilesOver(true);
        },
        [onAddAttachments, disabled, isEditing],
    );

    const handleDragLeave = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            if (
                event.relatedTarget instanceof Node &&
                event.currentTarget.contains(event.relatedTarget)
            ) {
                return;
            }
            setIsDraggingFilesOver(false);
        },
        [],
    );

    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            setIsDraggingFilesOver(false);
            if (!onAddAttachments || disabled || isEditing) {
                return;
            }
            const files = Array.from(event.dataTransfer.files ?? []);
            if (files.length === 0) {
                return;
            }
            event.preventDefault();
            onAddAttachments(files);
        },
        [onAddAttachments, disabled, isEditing],
    );

    const handleContainerMouseDown = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (disabled) {
                return;
            }

            const target = event.target as HTMLElement | null;
            if (!target) {
                return;
            }

            const isInteractiveTarget = Boolean(
                target.closest(
                    "button, a, input, textarea, [contenteditable='true']",
                ),
            );
            if (isInteractiveTarget) {
                return;
            }

            event.preventDefault();
            ReactEditor.focus(editor);
            if (!editor.selection) {
                Transforms.select(editor, Editor.end(editor, []));
            }
        },
        [disabled, editor],
    );

    const isOrganizationScopeFramed = isOrganizationScopedMessage && !isEditing;

    return (
        <div
            className={classNames({
                [styles.organizationScopeFrame]: isOrganizationScopeFramed,
            })}
        >
            {isOrganizationScopeFramed &&
                (hideOrganizationScopePicker ? (
                    <OrganizationScopeLabel />
                ) : (
                    <OrganizationScopePicker
                        attachedTo={editorContainerRef}
                        onIsInternalChange={handleIsInternalChange}
                    />
                ))}
            <div
                ref={editorContainerRef}
                data-base-ui-swipe-ignore
                onMouseDown={handleContainerMouseDown}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={classNames(
                    styles.slateEditorContainer,
                    {
                        [styles.slateEditorContainerFocused]:
                            isEditorFocused || isDropTarget,
                        [styles.slateEditorContainerDragOver]:
                            isDraggingFilesOver,
                    },
                    className,
                )}
            >
                {!isEditing &&
                    draftRefs &&
                    draftRefs.length > 0 &&
                    onRemoveDraftRef && (
                        <div className={styles.draftRefsList}>
                            {draftRefs.map((ref, idx) => (
                                <DraftRefTag
                                    key={buildRefKey(ref)}
                                    Component={DraftSpreadsheetRefTag}
                                    reference={ref}
                                    index={idx}
                                    onRemove={onRemoveDraftRef}
                                />
                            ))}
                        </div>
                    )}
                {!isEditing &&
                    draftTransactionRefs &&
                    draftTransactionRefs.length > 0 &&
                    onRemoveDraftTransactionRef && (
                        <div className={styles.draftRefsList}>
                            {draftTransactionRefs.map((ref, idx) => (
                                <DraftRefTag
                                    key={`${ref.taskId}::${ref.transactionId}`}
                                    Component={DraftTransactionRefTag}
                                    reference={ref}
                                    index={idx}
                                    onRemove={onRemoveDraftTransactionRef}
                                />
                            ))}
                        </div>
                    )}
                {!isEditing &&
                    draftCounterpartyRefs &&
                    draftCounterpartyRefs.length > 0 &&
                    onRemoveDraftCounterpartyRef && (
                        <div className={styles.draftRefsList}>
                            {draftCounterpartyRefs.map((ref, idx) => (
                                <DraftRefTag
                                    key={`${ref.taskId}::${ref.counterpartyId}`}
                                    Component={DraftCounterpartyRefTag}
                                    reference={ref}
                                    index={idx}
                                    onRemove={onRemoveDraftCounterpartyRef}
                                />
                            ))}
                        </div>
                    )}
                {!isEditing &&
                    draftInvoiceRefs &&
                    draftInvoiceRefs.length > 0 &&
                    onRemoveDraftInvoiceRef && (
                        <div className={styles.draftRefsList}>
                            {draftInvoiceRefs.map((ref, idx) => (
                                <DraftRefTag
                                    key={`${ref.taskId}::${ref.invoiceId}`}
                                    Component={DraftInvoiceRefTag}
                                    reference={ref}
                                    index={idx}
                                    onRemove={onRemoveDraftInvoiceRef}
                                />
                            ))}
                        </div>
                    )}
                {!isEditing &&
                    draftDocumentRefs &&
                    draftDocumentRefs.length > 0 &&
                    onRemoveDraftDocumentRef && (
                        <div className={styles.draftRefsList}>
                            {draftDocumentRefs.map((ref, idx) => (
                                <DraftRefTag
                                    key={`${ref.taskId}::${ref.documentId}`}
                                    Component={DraftDocumentRefTag}
                                    reference={ref}
                                    index={idx}
                                    onRemove={onRemoveDraftDocumentRef}
                                />
                            ))}
                        </div>
                    )}
                {!isEditing &&
                    pendingAttachments &&
                    pendingAttachments.length > 0 && (
                        <div className={styles.draftRefsList}>
                            {pendingAttachments.map((file, idx) => (
                                <PendingAttachmentTag
                                    key={fileKey(file)}
                                    file={file}
                                    index={idx}
                                    onRemove={onRemoveAttachment}
                                />
                            ))}
                        </div>
                    )}
                <Slate
                    editor={editor}
                    initialValue={initialValueRef.current}
                    onChange={handleChange}
                >
                    <HoveringToolbar
                        disabled={disabled}
                        onAddLink={() => setShowLinkModal(true)}
                    />
                    <Editable
                        className={styles.slateEditor}
                        renderLeaf={renderLeaf}
                        renderElement={renderElement}
                        placeholder={placeholder}
                        renderPlaceholder={renderPlaceholder}
                        disabled={disabled}
                        autoFocus={autoFocus}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsEditorFocused(true)}
                        onBlur={() => setIsEditorFocused(false)}
                    />
                    {displayActions && (
                        <div className={styles.actionsContainer}>
                            <HStack gap={4}>
                                {onAddAttachments && !isEditing && (
                                    <>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept={FINANCIAL_DOCUMENT_TYPES.join(
                                                ",",
                                            )}
                                            onChange={handleFileInputChange}
                                            className={styles.hiddenFileInput}
                                        />
                                        <ComposerInsertMenu
                                            disabled={disabled}
                                            onAttachFile={handleAttachFile}
                                            onToggleChecklist={
                                                handleToggleChecklist
                                            }
                                            onToggleBulletList={
                                                handleToggleBulletList
                                            }
                                            onToggleNumberedList={
                                                handleToggleNumberedList
                                            }
                                        />
                                    </>
                                )}
                                <Button
                                    iconOnly
                                    variant="secondary"
                                    size="md"
                                    disabled={disabled}
                                    onPointerDown={
                                        handleMentionButtonPointerDown
                                    }
                                    onClick={handleMentionButtonClick}
                                    aria-label="Add mention"
                                >
                                    <MentionIcon />
                                </Button>
                                <Button
                                    iconOnly
                                    variant="secondary"
                                    size="md"
                                    disabled={disabled}
                                    onPointerDown={handleSlashButtonPointerDown}
                                    onClick={handleSlashButtonClick}
                                    aria-label="Open commands"
                                >
                                    <SlashIcon />
                                </Button>
                            </HStack>
                            {!hideKeyboardShortcut && (
                                <Button
                                    iconOnly
                                    variant="primary"
                                    size="md"
                                    disabled={
                                        disabled ||
                                        !onSubmit ||
                                        (!hasEditorContent &&
                                            !hasPendingAttachments) ||
                                        isUploadingAttachments
                                    }
                                    onClick={submitMessage}
                                    aria-label="Send message"
                                >
                                    <ArrowUplongIcon />
                                </Button>
                            )}
                        </div>
                    )}
                </Slate>
                {/* Invisible anchor for mention popover */}
                <span ref={popoverTargetRef} className={styles.popoverTarget} />
                <Popover
                    open={isMentionPopoverOpen}
                    onOpenChange={(next) => {
                        if (!next) {
                            setMentionTarget(null);
                        }
                    }}
                >
                    <PopoverContent
                        align="start"
                        side="top"
                        sideOffset={4}
                        anchor={popoverTargetRef}
                        initialFocus={false}
                        className={styles.mentionPopover}
                    >
                        <div className={styles.mentionPopoverBody}>
                            {filteredMembers.map((member, i) => (
                                <div
                                    key={member.user.id}
                                    className={classNames(
                                        styles.mentionOption,
                                        {
                                            [styles.mentionOptionSelected]:
                                                i === mentionIndex,
                                        },
                                    )}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        selectMention(member);
                                    }}
                                    onMouseEnter={() => setMentionIndex(i)}
                                >
                                    <UserAvatar
                                        user={member.user}
                                        size="sm"
                                        aria-hidden
                                    />
                                    <span className={styles.mentionOptionName}>
                                        {member.user.preferredName ??
                                            member.user.firstName}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </PopoverContent>
                </Popover>
                {/* Invisible anchor for slash command popover */}
                <span
                    ref={slashPopoverTargetRef}
                    className={styles.popoverTarget}
                />
                <SlashCommandPopover
                    isOpen={isSlashPopoverOpen}
                    onClose={resetSlashState}
                    anchorRef={slashPopoverTargetRef}
                    filteredCommands={filteredCommands}
                    slashIndex={slashIndex}
                    setSlashIndex={setSlashIndex}
                    selectSlashCommand={selectSlashCommand}
                />
                <EntityRefPopover
                    isOpen={isEntityStage}
                    onClose={resetSlashState}
                    anchorRef={slashPopoverTargetRef}
                    options={entityOptions}
                    activeIndex={Math.min(
                        slashIndex,
                        Math.max(0, entityOptions.length - 1),
                    )}
                    setActiveIndex={setSlashIndex}
                    onSelect={selectEntityRef}
                    isLoading={isEntityLoading}
                    emptyText={
                        activeEntityKind
                            ? ENTITY_REF_EMPTY_COPY[activeEntityKind]
                            : ""
                    }
                />
                <LinkInputModal
                    show={showLinkModal}
                    onHide={() => setShowLinkModal(false)}
                    onSubmit={(url) => wrapLink(editor, url)}
                />
            </div>
        </div>
    );
};
